import { ArrowRightIcon, HandCoinsIcon } from 'lucide-react'
import React from 'react'

import { BottomSheet } from 'components/BottomSheet'
import { Button, IconLink, useBreakpoint } from 'slab'
import { type GrapeMoney } from 'types'
import { capitalize } from 'utils'
import type { MembersEnrollment } from 'utils/web_api.schemas'

import { RightDrawer } from '../../../RightDrawer'
import { enrollmentDisplayHelper } from '../../utils'
import { CarrierLogo } from '../CarrierLogo'
import EnrollmentProgress from '../EnrollmentProgress/EnrollmentProgress'
import EnrollmentsCarousel from '../EnrollmentsCarousel'
import FinancialStatusIndicator from '../FinancialStatusIndicator/FinancialStatusIndicator'
import TrackerSegment from '../TrackerSegment/TrackerSegment'
import { WhoPaysBadge } from '../WhoPaysBadge/WhoPaysBadge'

type EnrollmentCardProps = {
  enrollment: MembersEnrollment,
  allEnrollments: MembersEnrollment[],
  isPriorYear: boolean
}

export const makeInnerContent = (enrollment: MembersEnrollment, allEnrollments: MembersEnrollment[], isPriorYear: boolean, isMobile: boolean) => {
  const { startDate, endDate, showTracker, showFinancialStatus, showWhoPays } = enrollmentDisplayHelper(enrollment, isPriorYear)

  const putButtonBelow = !showTracker && !showFinancialStatus

  const viewDetailsButton = (
    <div className='-mr-2'>
      <Button
        rightIcon={ArrowRightIcon}
        variant='tertiary-deemphasized'
        size='small'
        style={{ paddingRight: '0.5rem', paddingLeft: '0.75rem' }}
      >
        <span className='body-sm-bold'>View details</span>
      </Button>
    </div>
  )

  const detailsCarousel = <EnrollmentsCarousel enrollments={allEnrollments} isPriorYear={isPriorYear} />

  const sheetOrDrawer = isMobile
    ? (
      <BottomSheet trigger={viewDetailsButton} showCloseButton zIndex={40}>
        <div className='py-2 px-4'>
          {detailsCarousel}
        </div>
      </BottomSheet>
      )
    : (
      <RightDrawer trigger={viewDetailsButton} width='large'>{detailsCarousel}</RightDrawer>
      )

  const whoPaysBadge = (
    <WhoPaysBadge thatchPays={enrollment.thatch_pays!} />
  )

  const today = new Date()
  const hasCurrentOrFutureCoverage = endDate && endDate >= today
  const consideredActive = hasCurrentOrFutureCoverage && enrollment.overview?.financial_status !== 'inactive'

  const component = (
    <>
      <div className='flex items-start justify-between gap-6 mb-4'>
        <div className='flex flex-col gap-1'>
          <div className='body-bold text-main'>{capitalize(enrollment.coverage_type)}</div>
          <div className='body-sm text-gray-500'>{enrollment.carrier_name}</div>
        </div>
        {enrollment.carrier_image_url && <CarrierLogo src={enrollment.carrier_image_url} />}
      </div>
      <div className='flex justify-between items-center gap-3 mb-4'>
        {showTracker && (
          <EnrollmentProgress
            state={enrollment.overview!.progress_state!}
            showCaption={false}
            startDate={startDate}
          />)}
        {showFinancialStatus && (
          <FinancialStatusIndicator
            status={enrollment.overview!.financial_status!}
            carrierName={enrollment.carrier_name}
            coverageEndDate={endDate}
            showCaption={false}
          />)}
        {putButtonBelow && showWhoPays && whoPaysBadge}
        {!putButtonBelow && sheetOrDrawer}
      </div>
      {showTracker && (
        <div className='flex gap-1 mb-4'>
          {enrollment.overview?.progress_events?.map((event) => (
            <TrackerSegment
              key={event.label}
              state={event.state}
              label={event.label}
              completionDate={event.completion_date ? new Date(event.completion_date) : undefined}
              progressState={enrollment.overview!.progress_state!}
            />
          ))}
        </div>
      )}
      <div>
        <hr className='border-gray-200 -mx-3 mb-4' />
        <div className='flex items-end justify-between gap-6 m-1'>
          <div className='flex flex-col gap-1'>
            <div className='body-sm text-gray-500'>Monthly premium</div>
            <div className='body text-main'>{enrollment.monthly_premium ? enrollment.monthly_premium.formatted_amount : '-'}</div>
          </div>
          {!putButtonBelow && showWhoPays && whoPaysBadge}
          {putButtonBelow && sheetOrDrawer}
        </div>
      </div>
    </>
  )

  return {
    component,
    consideredActive
  }
}

const MaxReimbursementAmount = ({ maxReimbursementAmount }: { maxReimbursementAmount: GrapeMoney }) => {
  return (
    <div className='flex items-center'>
      <HandCoinsIcon className='w-6 h-6 text-green-400 ml-3 mr-2' />
      <div className='body-sm text-gray-800'>
        <span className='body-sm-bold'>
          {maxReimbursementAmount.formatted_amount}
        </span> available for reimbursement
      </div>
      <IconLink icon={ArrowRightIcon} href='members/reimbursements/new' label='Submit reimbursement for this enrollment' />
    </div>
  )
}

const EnrollmentCard: React.FC<EnrollmentCardProps> = ({
  enrollment,
  allEnrollments,
  isPriorYear
}) => {
  const { breakpointIsAtMost } = useBreakpoint()

  const isMobile = breakpointIsAtMost('sm')

  const { component, consideredActive } = makeInnerContent(enrollment, allEnrollments, isPriorYear, isMobile)

  const bgColor = !isPriorYear && consideredActive ? 'bg-white' : 'bg-gray-50'

  return (
    <div className='flex flex-col gap-2 min-w-[18rem] w-full'>
      <div className={`border border-gray-200 rounded-2xl p-3 shadow-sm ${bgColor} flex flex-col`}>
        {component}
      </div>
      {enrollment.max_reimbursement_amount && enrollment.max_reimbursement_amount.subunits > 0 && (
        <MaxReimbursementAmount maxReimbursementAmount={enrollment.max_reimbursement_amount as GrapeMoney} />
      )}
    </div>
  )
}

export default EnrollmentCard
